import { faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { usePostHog } from 'posthog-js/react';
import { useCallback, useEffect, useRef } from 'react';
import { useLoaderData } from 'react-router-dom';

import { FaIconLite } from 'src/components/fa-icon-lite';
import { Player, VideoJS } from 'src/components/video-js';

import { usePosthogAttributionDataFromLoader } from './hooks';

import type { VideosContext } from './shared';

export function VideosEmbed() {
  const { object, videoPageInfo } = useLoaderData() as VideosContext;
  const posthog = usePostHog();
  const posthogData = usePosthogAttributionDataFromLoader();
  const playerRef = useRef<Player | null>(null);

  const messageListener = useCallback((event: MessageEvent) => {
    if (event.data === 'start-maverick-video' && playerRef.current) {
      const play = async (player: Player) => {
        try {
          // Browers may not let us trigger play through JS yet.
          await player.play();
        } catch (e) {
          console.log('autoplay failed', e);
        }
      };
      void play(playerRef.current);
    }
  }, []);

  const closeWindow = useCallback(() => {
    window.parent.postMessage('close', '*');
    if (playerRef.current) {
      if (!playerRef.current.paused()) {
        playerRef.current.pause();
      }
      playerRef.current.currentTime(0);
    }
    window.close();
  }, []);

  useEffect(() => {
    window.addEventListener('message', messageListener);
    return () => {
      window.removeEventListener('message', messageListener);
    };
  });

  return (
    <section className='mx-auto h-full bg-zinc-50'>
      <div className='relative mx-auto block h-dvh w-screen bg-white'>
        <div className='absolute right-0 top-0 z-10 m-4 cursor-pointer' onClick={closeWindow}>
          <FaIconLite icon={faCircleXmark} className='cursor-pointer justify-center rounded-full text-4xl text-white' />
        </div>
        <VideoJS
          options={{
            autoplay: false,
            controls: true,
            controlBar: {
              fullscreenToggle: false,
              pictureInPictureToggle: false,
            },
            disablePictureInPicture: true,
            enableSmoothSeeking: true,
            experimentalSvgIcons: true,
            fill: true,
            html5: {
              nativeControlsForTouch: true,
              nativeTextTracks: true,
              preloadTextTracks: false,
            },
            playsinline: true,
            poster: videoPageInfo.posterImgUrl ?? '',
            preload: 'auto',
            sources: [{ src: object.videoUrl, type: 'video/mp4' }],
            textTrackSettings: false,
            userActions: {
              doubleClick: false,
            },
          }}
          className='h-full'
          onReady={(player: Player) => {
            playerRef.current = player;
            player.on('play', function () {
              posthog.capture('customerVideo_videojsplay', posthogData);
            });
            player.on('pause', function () {
              posthog.capture('customerVideo_videojspause', posthogData);
            });
            player.on('canplay', function () {
              posthog.capture('customerVideo_videojscanplay', posthogData);
            });
            player.on('canplaythrough', function () {
              posthog.capture('customerVideo_videojscanplaythrough', posthogData);
            });
            player.on('ended', function () {
              posthog.capture('customerVideo_videojsended', posthogData);
              closeWindow();
            });
            player.on('loadeddata', function () {
              posthog.capture('customerVideo_videojsloadeddata', posthogData);
            });
          }}
        />
      </div>
    </section>
  );
}
